import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Sparkles, Loader2, TrendingUp, TrendingDown, Minus, AlertTriangle,
  CheckCircle, RefreshCw, FileText, ArrowRight, Lock, Clock, Zap,
} from "lucide-react";

const DECISION_LABELS = {
  renew: { label: "Renew", color: "bg-emerald-100 text-emerald-700", icon: RefreshCw },
  renegotiate: { label: "Renegotiate", color: "bg-amber-100 text-amber-700", icon: FileText },
  retender: { label: "Retender", color: "bg-orange-100 text-orange-700", icon: RefreshCw },
  terminate: { label: "Terminate", color: "bg-red-100 text-red-700", icon: AlertTriangle },
  expand_scope: { label: "Expand Scope", color: "bg-blue-100 text-blue-700", icon: TrendingUp },
};

const TREND_ICONS = { improving: TrendingUp, stable: Minus, declining: TrendingDown, insufficient_data: AlertTriangle };
const TREND_COLORS = { improving: "text-emerald-600", stable: "text-slate-500", declining: "text-red-500", insufficient_data: "text-amber-500" };

export function PremiumGate({ children }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-lg">
        <div className="text-center p-8 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mx-auto mb-4">
            <Lock size={28} className="text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Premium Feature</h3>
          <p className="text-sm text-slate-500 mb-4">
            Decision Intelligence is a premium feature that transforms performance data into actionable recommendations.
          </p>
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-sm px-3 py-1 text-xs">
            Contact your administrator to enable access
          </Badge>
        </div>
      </div>
      <div className="opacity-20 pointer-events-none select-none">{children}</div>
    </div>
  );
}

export function DecisionIntelligenceTab({ contractId }) {
  const { user } = useAuth();
  const hasPremium = user?.premium_features?.includes("decision_intelligence") || ["superuser", "admin"].includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [decideOpen, setDecideOpen] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ decision: "", rationale: "", follow_up_action: "none" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!hasPremium) { setLoading(false); return; }
    try {
      const res = await apiClient.get(`/decision-intelligence/${contractId}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Failed to load decision data");
    } finally { setLoading(false); }
  }, [contractId, hasPremium]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await apiClient.post(`/decision-intelligence/${contractId}/analyze`);
      setData(prev => ({ ...prev, latest_analysis: res.data }));
      toast.success("AI analysis complete");
    } catch (err) { toast.error(err.response?.data?.detail || "Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const handleDecide = async () => {
    if (!decisionForm.decision) return;
    setSaving(true);
    try {
      await apiClient.post(`/decision-intelligence/${contractId}/decide`, decisionForm);
      toast.success("Decision recorded");
      setDecideOpen(false);
      setDecisionForm({ decision: "", rationale: "", follow_up_action: "none" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record decision"); }
    finally { setSaving(false); }
  };

  const content = (
    <div className="space-y-4" data-testid="decision-intelligence-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber-500" />
          <span className="text-sm font-semibold text-slate-700">AI Decision Intelligence</span>
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-sm text-[9px] px-1.5">PREMIUM</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs" onClick={handleAnalyze} disabled={analyzing} data-testid="run-analysis-btn">
            {analyzing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} className="text-amber-500" />}
            {analyzing ? "Analyzing..." : "Run AI Analysis"}
          </Button>
          <Button size="sm" className="rounded-sm gap-1.5 text-xs bg-slate-900" onClick={() => setDecideOpen(true)} data-testid="record-decision-btn">
            <CheckCircle size={12} /> Record Decision
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-slate-300" size={24} /></div>
      ) : (
        <>
          {/* Latest Analysis */}
          {data?.latest_analysis?.analysis ? (
            <AnalysisCard analysis={data.latest_analysis} />
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Sparkles size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500 mb-2">No analysis yet. Run AI Analysis to get decision recommendations based on this contract's performance data.</p>
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-xs" onClick={handleAnalyze} disabled={analyzing} data-testid="run-first-analysis-btn">
                  {analyzing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Run First Analysis
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Decision Log */}
          {data?.decisions?.length > 0 && (
            <div data-testid="decision-log">
              <h4 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Decision Log</h4>
              <div className="space-y-2">
                {data.decisions.map(d => {
                  const info = DECISION_LABELS[d.decision] || DECISION_LABELS.renew;
                  return (
                    <div key={d.id} className="flex items-start gap-3 p-3 border rounded-sm" data-testid={`decision-entry-${d.id}`}>
                      <Badge className={`rounded-sm text-xs ${info.color}`}>{info.label}</Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700">{d.rationale || "No rationale provided"}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>{d.decided_by_name}</span>
                          <span>{new Date(d.created_at).toLocaleDateString()}</span>
                          {d.follow_up_action && d.follow_up_action !== "none" && (
                            <Badge variant="outline" className="text-[10px] rounded-sm gap-1">
                              <ArrowRight size={8} />{d.follow_up_action.replace("_", " ")}
                              {d.follow_up_status === "created" && <CheckCircle size={8} className="text-emerald-500" />}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Decision Dialog */}
      <Dialog open={decideOpen} onOpenChange={setDecideOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Contract Decision</DialogTitle>
            <DialogDescription>Log your decision for this contract based on the AI analysis and your professional judgment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Decision</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(DECISION_LABELS).map(([key, info]) => (
                  <Button
                    key={key}
                    variant={decisionForm.decision === key ? "default" : "outline"}
                    className={`rounded-sm gap-2 text-xs justify-start h-9 ${decisionForm.decision === key ? info.color : ""}`}
                    onClick={() => setDecisionForm(f => ({ ...f, decision: key }))}
                    data-testid={`decide-${key}`}
                  >
                    <info.icon size={14} /> {info.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Rationale</p>
              <Textarea
                placeholder="Explain the reasoning behind this decision..."
                value={decisionForm.rationale}
                onChange={e => setDecisionForm(f => ({ ...f, rationale: e.target.value }))}
                rows={3}
                className="rounded-sm text-sm"
                data-testid="decision-rationale"
              />
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Follow-Up Action</p>
              <Select value={decisionForm.follow_up_action} onValueChange={v => setDecisionForm(f => ({ ...f, follow_up_action: v }))}>
                <SelectTrigger className="rounded-sm text-sm h-9" data-testid="follow-up-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No automated action</SelectItem>
                  <SelectItem value="generate_notice">Generate Notice (draft)</SelectItem>
                  <SelectItem value="create_addendum">Create Addendum (draft)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleDecide} disabled={saving || !decisionForm.decision} className="rounded-sm gap-1.5 bg-slate-900" data-testid="confirm-decision-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Confirm Decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (!hasPremium) return <PremiumGate>{content}</PremiumGate>;
  return content;
}

function AnalysisCard({ analysis }) {
  const a = analysis.analysis || {};
  const rec = a.primary_recommendation || "renew";
  const info = DECISION_LABELS[rec] || DECISION_LABELS.renew;
  const trend = a.performance_trend || "insufficient_data";
  const TrendIcon = TREND_ICONS[trend] || Minus;
  const trendColor = TREND_COLORS[trend] || "text-slate-500";
  const alt = a.alternative_recommendation || "";

  return (
    <Card className="border-l-4 border-l-amber-400" data-testid="analysis-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500" /> AI Recommendation
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={`rounded-sm text-sm font-semibold ${info.color}`}>{info.label}</Badge>
            {a.confidence && <Badge variant="outline" className="rounded-sm text-[10px]">{a.confidence} confidence</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Performance Trend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-sm">
          <div className="flex items-center gap-2">
            <TrendIcon size={18} className={trendColor} />
            <span className="text-sm font-medium capitalize">{trend.replace("_", " ")}</span>
          </div>
          {a.key_metrics_summary && <p className="text-xs text-slate-500 flex-1">{a.key_metrics_summary}</p>}
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Clock size={10} />
            <span>{new Date(analysis.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Reasoning */}
        {a.reasoning && (
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">Reasoning</p>
            <ul className="space-y-1">
              {(Array.isArray(a.reasoning) ? a.reasoning : [a.reasoning]).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-amber-500 mt-1 flex-shrink-0">&#x2022;</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risk Factors */}
        {a.risk_factors && (
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">Risk Factors</p>
            <div className="text-sm text-slate-600">
              {Array.isArray(a.risk_factors) ? (
                <ul className="space-y-1">{a.risk_factors.map((r, i) => (
                  <li key={i} className="flex items-start gap-2"><AlertTriangle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" /><span>{r}</span></li>
                ))}</ul>
              ) : <p>{a.risk_factors}</p>}
            </div>
          </div>
        )}

        {/* Alternative */}
        {alt && (
          <div className="p-3 border rounded-sm bg-slate-50/50">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Alternative Option</p>
            <p className="text-sm text-slate-600">{typeof alt === "object" ? JSON.stringify(alt) : alt}</p>
          </div>
        )}

        {/* Action Items */}
        {a.action_items && Array.isArray(a.action_items) && a.action_items.length > 0 && (
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">Recommended Next Steps</p>
            <div className="space-y-1">
              {a.action_items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Financial Context Note */}
        {a.financial_context_note && (
          <div className="p-3 border border-amber-200 bg-amber-50/50 rounded-sm">
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              {a.financial_context_note}
            </p>
          </div>
        )}

        {/* Data Sources */}
        <div className="flex gap-4 text-[10px] text-slate-400 pt-1 border-t">
          <span>Performance logs: {analysis.performance_data_count}</span>
          <span>Obligations: {analysis.obligations_count}</span>
          <span>KPIs: {analysis.kpis_count}</span>
        </div>
      </CardContent>
    </Card>
  );
}
